import { Field } from "@/components/form-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { PatchChallengeInput, Visibility } from "@/lib/api";

import { SectionCard } from "./section-card";
import { changed, useChallenge, useChallengeState } from "./use-challenge";

/** Name, what students read, who sees it, and whether it is taking runs. */
export function AdminChallengeBasics() {
  const { challenge, refetch } = useChallenge();
  const [form, set, reset] = useChallengeState((c) => ({
    name: c.name,
    description: c.description,
    final_submission_link: c.final_submission_link,
    visibility: c.visibility as Visibility,
    active: c.active,
  }));

  const patch: PatchChallengeInput = {};
  if (form.name.trim() !== challenge.name) patch.name = form.name.trim();
  if (form.description !== challenge.description) patch.description = form.description;
  if (form.final_submission_link.trim() !== challenge.final_submission_link)
    patch.final_submission_link = form.final_submission_link.trim();
  if (changed(form.visibility, challenge.visibility)) patch.visibility = form.visibility;
  if (form.active !== challenge.active) patch.active = form.active;

  const graded = challenge.type === "competition";
  // A challenge that collects its report on this site needs no external form.
  const needsForm = graded && !challenge.collects_report;
  const problem = !form.name.trim()
    ? "A name is required."
    : needsForm && !form.final_submission_link.trim()
      ? "A graded challenge needs its submission form link, or the report collected on this site (Report section)."
      : null;

  return (
    <SectionCard
      title="Basics"
      description="What it is, what students read about it, and who may see it. The type and where it runs were fixed at creation."
      patch={patch}
      problem={problem}
      onSaved={refetch}
      onReset={reset}
    >
      <Field id="b-name" label="Name" required>
        <Input id="b-name" value={form.name} onChange={(e) => set((f) => ({ ...f, name: e.target.value }))} />
      </Field>

      <Field
        id="b-desc"
        label="Description"
        hint="The first thing under the title on the challenge page."
      >
        <textarea
          id="b-desc"
          rows={4}
          value={form.description}
          onChange={(e) => set((f) => ({ ...f, description: e.target.value }))}
          className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 w-full resize-y rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-3"
        />
      </Field>

      <Field
        id="b-form"
        label="Official submission form"
        required={needsForm}
        hint={
          challenge.collects_report
            ? "Not needed: the report is handed in on the challenge page. Leave blank."
            : graded
              ? "Graded challenges are marked off this form."
              : "Optional for a practice challenge."
        }
      >
        <Input
          id="b-form"
          value={form.final_submission_link}
          onChange={(e) => set((f) => ({ ...f, final_submission_link: e.target.value }))}
          placeholder="https://forms.google.com/..."
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="b-visibility" label="Visibility" hint="Public challenges expose names and emails on their leaderboard. Staff only hides the challenge from students while it is being prepared.">
          <Select
            items={{ staff: "Staff only", enrolled: "Enrolled only", public: "Public" }}
            value={form.visibility}
            onValueChange={(v) => {
              if (typeof v === "string") set((f) => ({ ...f, visibility: v as Visibility }));
            }}
          >
            <SelectTrigger id="b-visibility" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">Staff only</SelectItem>
              <SelectItem value="enrolled">Enrolled only</SelectItem>
              <SelectItem value="public">Public</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <div className="flex items-start gap-3 rounded-md border p-3">
          <Switch
            id="b-active"
            checked={form.active}
            onCheckedChange={(active) => set((f) => ({ ...f, active }))}
            className="mt-0.5"
          />
          <div>
            <Label htmlFor="b-active">Accepting runs</Label>
            <p className="text-muted-foreground text-xs">
              Off refuses new sessions and uploads; the leaderboard stays readable.
            </p>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
